'use client';

import { AlertCircleOutline, CheckCircleOutline, Star, Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Button } from '~/components/Button';
import styles from './NotificationBanner.module.css';
import type { NotificationBannerProps } from './NotificationBanner.types';

const INTENT_ICONS = {
  positive: CheckCircleOutline,
  warning: AlertCircleOutline,
  highlight: Star
};

const NotificationBanner = ({
  intent,
  title,
  children,
  onClose,
  dataTestId = 'NotificationBanner'
}: Readonly<NotificationBannerProps>) => {
  const t = useTranslations('aria');
  const Icon = INTENT_ICONS[intent];
  const Text = intent === 'positive' ? 'output' : 'div';

  return (
    <div
      role={intent === 'warning' ? 'alert' : undefined}
      className={clsx(styles.banner, styles[intent], !!children && styles.hasBody)}
      data-test-id={dataTestId}
    >
      <span className={styles.icon}>
        <Icon decorative size={intent === 'highlight' ? 'scale' : 'large'} />
      </span>
      <Text className={styles.text}>
        <span className={styles.title}>{title}</span>
        {children && <span className={styles.body}>{children}</span>}
      </Text>
      {onClose && (
        <Button variant="icon" isTransparent ariaLabel={t('close')} onPress={onClose}>
          <Xmark decorative size="large" />
        </Button>
      )}
    </div>
  );
};

export { NotificationBanner };
